import React, { useCallback } from 'react';
import { Menu, Badge, Flex, theme } from '@robinpowered/ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faQuestionCircle,
  faCommentDots,
  faRocket,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { navigationIds } from '@robinpowered/dashboard-apps-common';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { generateLabel } from '../../lib/utility';
import { RobinAvatar } from '../Avatar/RobinAvatar';
import { User } from '../../contexts/AuthContext';
import { useAmplitude } from '../../contexts/AmplitudeContext';
import { AmplitudeEvents } from '../../constants/amplitudeEvents';
import { config } from '../../config';
import type { OnboardingProgress } from '../../hooks/useOnboardingProgress';

const ICON_WIDTH = 16;

// Extend window interface for Intercom and Pendo
declare global {
  interface Window {
    Intercom?: (action: string) => void;
    pendo?: {
      showGuideById?: (guideId: string) => void;
      showGuideByName?: (guideName: string) => void;
      feedback?: {
        open?: () => void;
      };
    };
  }
}

type BottomMenuProps = {
  name?: string;
  avatar?: string;
  collapsed?: boolean;
  showNotifications?: boolean;
  hasPendingApprovalNotifications?: boolean;
  activeOrgSlug?: string;
  activeKeys?: string[];
  currentUser?: User | null;
  showPendo?: boolean;
  gettingStartedLink?: string;
  onboardingProgress?: OnboardingProgress | null;
  showIntercom?: boolean;
};

// Helper functions to create menu items
const createSupportItem = (t: (key: string) => string) => ({
  key: 'support',
  id: navigationIds.SUPPORT_NAV_LINK,
  icon: (
    <FontAwesomeIcon icon={faQuestionCircle} color="white" width={ICON_WIDTH} />
  ),
  label: t('support'),
});

const createFeedbackItem = (t: (key: string) => string) => ({
  key: 'giveFeedback',
  id: navigationIds.GIVE_FEEDBACK_NAV_LINK,
  icon: (
    <FontAwesomeIcon icon={faCommentDots} color="white" width={ICON_WIDTH} />
  ),
  label: t('giveFeedback'),
});

const createNotificationsItem = (
  t: (key: string) => string,
  activeOrgSlug?: string,
  hasPendingApprovalNotifications = false
) => {
  if (!activeOrgSlug) {
    return null;
  }

  return {
    key: 'notifications',
    icon: (
      <Badge
        style={{ height: 6, width: 6 }}
        dot={hasPendingApprovalNotifications}
      >
        <FontAwesomeIcon icon={faBell} width={ICON_WIDTH} />
      </Badge>
    ),
    label: generateLabel({
      label: t('notifications'),
      route: `${config.dashboardUrl}/${activeOrgSlug}/notifications`,
      isExternal: true,
    }),
  };
};

const createFinishSetupItem = (
  t: (key: string) => string,
  gettingStartedLink: string,
  navigate: (to: string) => void,
  collapsed?: boolean,
  progress?: OnboardingProgress | null,
  fontColor?: string,
  backgroundColor?: string,
  trackEvent?: (eventName: string, metadata?: Record<string, unknown>) => void
) => ({
  key: 'finishSetup',
  id: navigationIds.FINISH_SETUP_NAV_LINK,
  icon: collapsed && <FontAwesomeIcon icon={faRocket} />,
  label: (
    <Flex align="center" gap={8}>
      <FontAwesomeIcon icon={faRocket} />
      {t('setup')}
      <Badge
        // eslint-disable-next-line react/jsx-no-literals
        count={`${progress?.completedCount ?? 0}/${progress?.totalSteps ?? 0}`}
        styles={{
          root: {
            marginLeft: 'auto',
          },
        }}
        style={{
          backgroundColor: backgroundColor,
          color: fontColor,
          fontWeight: 600,
          fontSize: 12,
          boxShadow: 'none',
        }}
      />
    </Flex>
  ),
  onClick: () => {
    trackEvent?.(AmplitudeEvents.CLICK_GETTING_STARTED_NAV, {
      route: gettingStartedLink,
    });
    navigate(gettingStartedLink);
  },
});

const createProfileMenuItems = (
  t: (key: string) => string,
  activeOrgSlug?: string,
  currentUser?: User | null,
  trackEvent?: (eventName: string, metadata?: Record<string, unknown>) => void
) => {
  if (!activeOrgSlug || !currentUser?.id) {
    return [];
  }

  return [
    {
      key: 'userProfileLink',
      label: generateLabel({
        label: t('profile'),
        route: `${config.dashboardUrl}/${activeOrgSlug}/people/${currentUser.id}`,
        isExternal: true,
      }),
    },
    {
      key: 'setHomepage',
      label: generateLabel({
        label: t('setHomepage'),
        route: `${config.dashboardUrl}/account#office-preferences`,
        isExternal: true,
      }),
      onClick: () => {
        trackEvent?.(AmplitudeEvents.CLICK_SET_HOMEPAGE);
      },
    },
    {
      key: 'userSettings',
      label: generateLabel({
        label: t('userSettings'),
        route: `${config.dashboardUrl}/account`,
        isExternal: true,
      }),
    },
    {
      key: 'help',
      label: t('help'),
      children: [
        {
          key: 'helpMenu.helpCenter',
          label: generateLabel({
            label: t('helpMenu.helpCenter'),
            route: 'https://support.robinpowered.com',
            isExternal: true,
          }),
        },
        {
          key: 'helpMenu.contactUs',
          label: generateLabel({
            label: t('helpMenu.contactUs'),
            route: activeOrgSlug
              ? `${config.dashboardUrl}/${activeOrgSlug}/premier-support`
              : `${config.dashboardUrl}/premier-support`,
            isExternal: true,
          }),
        },
        {
          key: 'helpMenu.submitRequest',
          label: generateLabel({
            label: t('helpMenu.submitRequest'),
            route: 'mailto:support@robinpowered.com',
            isExternal: true,
          }),
        },
        {
          key: 'helpMenu.status',
          label: generateLabel({
            label: t('helpMenu.status'),
            route: 'https://status.robinpowered.com',
            isExternal: true,
          }),
        },
      ],
    },
    {
      key: 'helpMenu.productUpdates',
      label: generateLabel({
        label: t('helpMenu.productUpdates'),
        route:
          'https://support.robinpowered.com/hc/en-us/categories/32826409804173-Product-updates',
        isExternal: true,
      }),
    },
    {
      key: 'getRobinApps',
      label: generateLabel({
        label: t('getApps'),
        route: 'https://robinpowered.com/downloads',
        isExternal: true,
      }),
    },
    {
      type: 'divider',
      key: 'divider',
    },
    {
      key: 'logout',
      label: generateLabel({
        label: t('logout'),
        route: `${config.dashboardUrl}/logout`,
        isExternal: true,
      }),
    },
  ];
};

export const BottomMenu = ({
  name,
  avatar,
  collapsed,
  showNotifications = false,
  hasPendingApprovalNotifications = false,
  activeOrgSlug,
  activeKeys = [],
  currentUser,
  showPendo = false,
  gettingStartedLink,
  onboardingProgress,
  showIntercom = false,
}: BottomMenuProps) => {
  const { t } = useTranslation('globalNavigation');
  const { useToken } = theme;
  const { token } = useToken();
  const { trackEvent } = useAmplitude();
  const navigate = useNavigate();

  const handleMenuClick = useCallback(
    ({
      key,
      domEvent,
    }: {
      key: string;
      domEvent: React.MouseEvent | React.KeyboardEvent;
    }) => {
      // Native DOM click listeners from Intercom/Pendo already handle mouse clicks.
      // Only intervene for keyboard activation where native click doesn't fire.
      if (domEvent.type !== 'keydown') return;

      if (key === 'support') {
        document.getElementById(navigationIds.SUPPORT_NAV_LINK)?.click();
      }
      if (key === 'giveFeedback') {
        document.getElementById(navigationIds.GIVE_FEEDBACK_NAV_LINK)?.click();
      }
    },
    []
  );

  // Build menu items using helper functions
  const menuItems = [
    ...(showIntercom && window.Intercom ? [createSupportItem(t)] : []),

    // Feedback item (only if Pendo is enabled AND available on window)
    ...(showPendo && window.pendo ? [createFeedbackItem(t)] : []),

    // Notifications item (only if enabled and org slug exists)
    ...(showNotifications && activeOrgSlug
      ? [
          createNotificationsItem(
            t,
            activeOrgSlug,
            hasPendingApprovalNotifications
          ),
        ]
      : []),

    // Finish setup item — shown above the profile when onboarding is in progress
    ...(gettingStartedLink
      ? [
          createFinishSetupItem(
            t,
            gettingStartedLink,
            navigate,
            collapsed,
            onboardingProgress,
            token.colorPrimary,
            token.colorWhite,
            trackEvent
          ),
        ]
      : []),

    // Profile menu
    {
      key: 'profileMenu',
      icon: (
        <RobinAvatar
          avatar={avatar}
          name={name}
          style={{ marginRight: 8, marginLeft: collapsed ? -6 : 0 }}
        />
      ),
      // TODO: For some reason, this tooltip does not function properly
      // when collapsed is true. Fix later so name is shown in tooltip
      label: collapsed ? null : name,
      children: createProfileMenuItems(
        t,
        activeOrgSlug,
        currentUser,
        trackEvent
      ),
    },
  ];

  return (
    <Menu
      mode="vertical"
      triggerSubMenuAction="click"
      theme="dark"
      style={{
        marginBottom: token.marginSM,
        // NOTE: This padding is a small hack to align this 'vertical' menu with the
        // 'inline' menu items below it.
        paddingLeft: collapsed ? '0px' : '8px',
      }}
      selectedKeys={activeKeys}
      items={menuItems}
      onClick={handleMenuClick}
    />
  );
};
